import { memo, useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import "./SmartDPHeroCommercialTag.scss";
import classNames from "classnames";
import { CheckmarkCircleStrokeIcon, CopyContentIcon } from "app/icons/navigation";

const SmartDPHeroCommercialTag = ({ tag, enableCopy = false }) => {
	const [copied, setCopied] = useState(false);
	const textRef = useRef(null);

	const copyText = useCallback(event => {
		if (event?.stopPropagation) {
			event.stopPropagation();
		}

		if (!tag) {
			return;
		}

		if (!navigator?.clipboard?.writeText) {
			return;
		}

		navigator.clipboard
			.writeText(tag)
			.then(() => {
				setCopied(true);
				setTimeout(() => {
					setCopied(false);
				}, 2000);
			})
			.catch(() => {});
	}, []);

	const handleKeyDown = useCallback(
		event => {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				copyText(event);
			}
		},
		[copyText]
	);

	if (!tag) {
		return null;
	}

	const icon = copied ? (
		<CheckmarkCircleStrokeIcon width={20} height={20} />
	) : (
		<CopyContentIcon width={20} height={20} />
	);

	return (
		<Typography
			variant={TYPOGRAPHY_VARIANTS.REGULAR}
			variantMD={TYPOGRAPHY_VARIANTS.XL}
			variantLG={TYPOGRAPHY_VARIANTS.XL3}
			isBold
			className={classNames("smartDP-hero-commercial__tag", {
				"smartDP-hero-commercial__tag--copyable": enableCopy,
			})}
			data-testid="smartDP-hero-commercial-tag"
			component="div"
			tabIndex={0}
			{...enableCopy && {
				ariaLabel: "Copy to clipboard",
				role: "button",
				onClick: copyText,
				onKeyDown: handleKeyDown,
			}}
		>
			<span ref={textRef}>{tag}</span>
			{enableCopy && <span className="smartDP-hero-commercial__tag-icon">{icon}</span>}
		</Typography>
	);
};

SmartDPHeroCommercialTag.propTypes = {
	tag: PropTypes.node,
	enableCopy: PropTypes.bool,
};

export default memo(SmartDPHeroCommercialTag);
